import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, ListChecks, FileSpreadsheet, Mail, Settings, Sparkles, History, Search, Plus, MoreVertical, Shield, CheckCircle2, XCircle, ExternalLink, Save, ToggleLeft, ToggleRight } from 'lucide-react';
type AdminTab = 'users' | 'competencies' | 'questions' | 'templates' | 'emails' | 'platform' | 'ai' | 'audit';
export function Administration() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const tabs = [{
    id: 'users',
    label: 'User Management',
    icon: Users
  }, {
    id: 'competencies',
    label: 'Competency Management',
    icon: Target
  }, {
    id: 'questions',
    label: 'Question Management',
    icon: ListChecks
  }, {
    id: 'templates',
    label: 'Assessment Templates',
    icon: FileSpreadsheet
  }, {
    id: 'emails',
    label: 'Email Templates',
    icon: Mail
  }, {
    id: 'platform',
    label: 'Platform Settings',
    icon: Settings
  }, {
    id: 'ai',
    label: 'AI Settings',
    icon: Sparkles
  }, {
    id: 'audit',
    label: 'Audit Logs',
    icon: History
  }] as const;
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-182" data-mp="1vs4p2wwi5len">
      {/* Header */}
      <div data-id="element-183" data-mp="2jhyyt5liytuo">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center" data-id="element-184" data-mp="2jhfi1s7m2bi8">
          <Shield className="h-6 w-6 mr-3 text-slate-700" data-id="element-185" data-mp="2oh26kxsw3e6c" />
          Administration
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-186" data-mp="29s5qcdgoiuun">
          Manage system configurations, users, and platform-wide settings.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6" data-id="element-187" data-mp="2eq79eye3uu64">
        {/* Left Sidebar Nav */}
        <div className="w-full md:w-64 shrink-0" data-id="element-188" data-mp="32gl0omov8ozn">
          <nav className="space-y-1" data-id="element-189" data-mp="32eqjnjgpth0u">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-190" data-mp="3h2qrlyfcxq4o">
                  <Icon className={`mr-3 h-4 w-4 ${isActive ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-191" data-mp="2y57i6tozm5hq" />
                  {tab.label}
                </button>;
          })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0" data-id="element-192" data-mp="2kdj6usmkbl1t">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden min-h-[500px]" data-id="element-193" data-mp="2kekhivzo25j0">
            {activeTab === 'users' && <UserManagementPanel data-id="element-194" data-mp="2fg0wcxswcxrp" />}
            {activeTab === 'competencies' && <CompetencyManagementPanel data-id="element-195" data-mp="2aflkybs6e0cd" />}
            {activeTab === 'questions' && <QuestionManagementPanel data-id="element-196" data-mp="pxu790f5967t" />}
            {activeTab === 'templates' && <AssessmentTemplatesPanel data-id="element-197" data-mp="uw5gpa1j1epi" />}
            {activeTab === 'emails' && <EmailTemplatesPanel data-id="element-198" data-mp="uuaxq19tfwo2" />}
            {activeTab === 'platform' && <PlatformSettingsPanel data-id="element-199" data-mp="zw9zj6pwqliw" />}
            {activeTab === 'ai' && <AISettingsPanel data-id="element-200" data-mp="13wtv45uu1h2x" />}
            {activeTab === 'audit' && <AuditLogsPanel data-id="element-201" data-mp="13wadyrd0awpy" />}
          </div>
        </div>
      </div>
    </div>;
}
// --- Individual Panels ---
function UserManagementPanel() {
  return <div className="flex flex-col h-full" data-id="element-202" data-mp="c54kgtrsjx9n">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-203" data-mp="c65ucux2mdps">
        <div data-id="element-204" data-mp="g5oevpkzcms3">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-205" data-mp="g550giujggit">
            User Management
          </h2>
          <p className="text-sm text-slate-500" data-id="element-206" data-mp="kzsqwn93rl01">
            Manage administrators, managers, and system access.
          </p>
        </div>
        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-207" data-mp="3om9h8nj3whm8">
          <Plus className="h-4 w-4 mr-2" data-id="element-208" data-mp="35gbsavqmzz38" />
          Invite User
        </button>
      </div>
      <div className="p-4 border-b border-slate-200" data-id="element-209" data-mp="30l641q9ri8bz">
        <div className="relative max-w-md" data-id="element-210" data-mp="3f8hfzt9uvucm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-211" data-mp="3fac41ggfc2kx" />
          <input type="text" placeholder="Search users by name or email..." className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:bg-white focus:border-slate-300 outline-none" data-id="element-212" data-mp="3afy4d1uqb9ag" />
        </div>
      </div>
      <div className="flex-1 overflow-auto" data-id="element-213" data-mp="20o31vyny83q">
        <table className="w-full text-sm text-left" data-id="element-214" data-mp="1zd2e50ilggu">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-215" data-mp="3t9bsan00yfgk">
            <tr data-id="element-216" data-mp="23tvlhe9kp5tu">
              <th className="px-6 py-3 font-medium" data-id="element-217" data-mp="23tcegslgo1ul">User</th>
              <th className="px-6 py-3 font-medium" data-id="element-218" data-mp="1yurd2puh3c4k">Role</th>
              <th className="px-6 py-3 font-medium" data-id="element-219" data-mp="1ywc78ecvkt1w">Status</th>
              <th className="px-6 py-3 font-medium text-right" data-id="element-220" data-mp="ecgtwx8z9m2v">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-221" data-mp="ja8k9mtz504y">
            {[{
            name: 'Jane Doe',
            email: 'jane@example.com',
            role: 'System Admin',
            status: 'Active'
          }, {
            name: 'Michael Scott',
            email: 'michael@example.com',
            role: 'Manager',
            status: 'Active'
          }, {
            name: 'Sarah Connor',
            email: 'sarah@example.com',
            role: 'Manager',
            status: 'Pending'
          }].map((user, i) => <tr key={i} className="hover:bg-slate-50" data-id="element-222" data-mp="1g0slin185csw" data-mpi={i}>
                <td className="px-6 py-4" data-id="element-223" data-mp="1fz7rqzmqhxw3">
                  <div className="font-medium text-slate-900" data-id="element-224" data-mp="1l0neqx8h2eh4">{user.name}</div>
                  <div className="text-slate-500" data-id="element-225" data-mp="1jiyjq72mo3n2">{user.email}</div>
                </td>
                <td className="px-6 py-4" data-id="element-226" data-mp="1ek54a93s62k9">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-227" data-mp="1ej3ppunk2xxs">
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-228" data-mp="14niklnn6xhjt">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-229" data-mp="229py5n1h2k8b">
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-230" data-mp="1nllg8la8ucql">
                  <button className="text-slate-400 hover:text-slate-600" data-id="element-231" data-mp="1niqrwio407rw">
                    <MoreVertical className="h-4 w-4" data-id="element-232" data-mp="2l1s70c7vgw80" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function CompetencyManagementPanel() {
  return <div className="p-8 text-center max-w-lg mx-auto mt-12" data-id="element-233" data-mp="2sjqc84j0tl5f">
      <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-234" data-mp="2sk07a6ixaihz">
        <Target className="h-8 w-8 text-indigo-600" data-id="element-235" data-mp="2xhi97cyivxgh" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-236" data-mp="32ixqctxkxjtl">
        Competency Domains
      </h2>
      <p className="text-slate-500 mb-8" data-id="element-237" data-mp="37fxv91ip0gj4">
        Each role carries a standard set of competency domains. Every domain
        offers Characteristic and Behaviour questions, from which subscribers
        choose two of each per cycle.
      </p>
      <Link to="/app/competencies" className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-238" data-mp="3gbng99g8b8vz">
        Open Competency Domains
        <ExternalLink className="h-4 w-4 ml-2" data-id="element-239" data-mp="3v622div3ayev" />
      </Link>
    </div>;
}
function QuestionManagementPanel() {
  return <div className="p-8 text-center max-w-lg mx-auto mt-12" data-id="element-240" data-mp="kat9x1xda0f9">
      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-241" data-mp="fdb8rxplcpht">
        <ListChecks className="h-8 w-8 text-blue-600" data-id="element-242" data-mp="tckhkcbgwhbr" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-243" data-mp="taq1bbb55de0">
        Question Bank
      </h2>
      <p className="text-slate-500 mb-8" data-id="element-244" data-mp="obmv3njfd14g">
        Questions are managed centrally to ensure consistency across
        assessments. You can create templates, categorize by rating type, and
        bulk import questions in the dedicated library.
      </p>
      <Link to="/app/questions" className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-245" data-mp="aezzpp74ej0n">
        Open Question Library
        <ExternalLink className="h-4 w-4 ml-2" data-id="element-246" data-mp="3e1gq1ph4jfmu" />
      </Link>
    </div>;
}
function AssessmentTemplatesPanel() {
  return <div className="flex flex-col h-full" data-id="element-247" data-mp="39245xchn0mny">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-248" data-mp="1okv1bycda18q">
        <div data-id="element-249" data-mp="1tdo5ak00h5jr">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-250" data-mp="1tdfpaj8ldk4c">
            Assessment Templates
          </h2>
          <p className="text-sm text-slate-500" data-id="element-251" data-mp="1fn2kgpqzylj8">
            Standardized frameworks for quick assessment creation.
          </p>
        </div>
        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-252" data-mp="28a6j8czhy3mk">
          <Plus className="h-4 w-4 mr-2" data-id="element-253" data-mp="2cayufbrxiilh" />
          New Template
        </button>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-254" data-mp="1yb7j5issrgb6">
        {[{
        name: 'Standard 360° Executive Review',
        type: '360 Review',
        questions: 45
      }, {
        name: 'Quarterly Manager Check-in',
        type: 'Self/Manager',
        questions: 15
      }, {
        name: 'Peer Feedback Pulse',
        type: 'Peer Only',
        questions: 8
      }].map((template, i) => <div key={i} className="border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors cursor-pointer group" data-id="element-255" data-mp="1b0ewnn1hoed6" data-mpi={i}>
            <div className="flex justify-between items-start mb-3" data-id="element-256" data-mp="11a3dwud5c0m8">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600 group-hover:bg-slate-900 group-hover:text-white transition-colors" data-id="element-257" data-mp="nbluh8u9xaa4">
                <FileSpreadsheet className="h-5 w-5" data-id="element-258" data-mp="rdqklxlijos0" />
              </div>
              <button className="text-slate-400 hover:text-slate-600" data-id="element-259" data-mp="ddpp6xo0p9jx">
                <MoreVertical className="h-4 w-4" data-id="element-260" data-mp="ic99yuqvkfap" />
              </button>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1" data-id="element-261" data-mp="ie3u46ubo3dq">
              {template.name}
            </h3>
            <div className="flex items-center text-xs text-slate-500 space-x-3" data-id="element-262" data-mp="2cqa4x4jb0i5i">
              <span data-id="element-263" data-mp="2co7g2ntw0v2d">{template.type}</span>
              <span data-id="element-264" data-mp="125crsph58y3">•</span>
              <span data-id="element-265" data-mp="13gfrqb3ico5">{template.questions} Questions</span>
            </div>
          </div>)}
      </div>
    </div>;
}
function EmailTemplatesPanel() {
  return <div className="flex flex-col h-full" data-id="element-266" data-mp="aszzey6muak2">
      <div className="p-5 border-b border-slate-200 bg-slate-50" data-id="element-267" data-mp="au2r38bs4h11">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-268" data-mp="6104xl9c09uv">
          Email Templates
        </h2>
        <p className="text-sm text-slate-500" data-id="element-269" data-mp="jsepvxmsul4k">
          Customize automated system communications.
        </p>
      </div>
      <div className="flex-1 flex" data-id="element-270" data-mp="ettqu1jaa7hp">
        <div className="w-1/3 border-r border-slate-200 overflow-y-auto" data-id="element-271" data-mp="trlyk7il9rlc">
          <div className="divide-y divide-slate-100" data-id="element-272" data-mp="tpjcfykk4ilw">
            {['Assessment Invitation', 'Reminder: 3 Days Left', 'Reminder: 24 Hours', 'Assessment Completed', 'Report Ready'].map((tpl, i) => <button key={i} className={`w-full text-left px-4 py-3 text-sm transition-colors ${i === 0 ? 'bg-indigo-50 text-indigo-700 font-medium border-l-2 border-indigo-600' : 'text-slate-600 hover:bg-slate-50 border-l-2 border-transparent'}`} data-id="element-273" data-mp="2qlqkitrl3rf3" data-mpi={i}>
                {tpl}
              </button>)}
          </div>
        </div>
        <div className="flex-1 p-6 bg-slate-50/50" data-id="element-274" data-mp="3u8lvz3kt31fn">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-id="element-275" data-mp="31hvkrj62dg7">
            <div className="p-4 border-b border-slate-200" data-id="element-276" data-mp="2zoursv8ing4">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-277" data-mp="71s86n1gs241">
                Subject Line
              </label>
              <input type="text" defaultValue="Action Required: 360° Leadership Assessment for {{target_name}}" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-indigo-100 outline-none" data-id="element-278" data-mp="bvwfj7en6c5n" />
            </div>
            <div className="p-4" data-id="element-279" data-mp="2b94szyeuj1r2">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-280" data-mp="2b9eizlzw58wr">
                Email Body
              </label>
              <textarea rows={8} defaultValue={`Hi {{respondent_name}},\n\nYou have been invited to participate in a 360° Leadership Assessment for {{target_name}}.\n\nYour feedback is crucial for their professional development. Please complete the assessment by {{due_date}}.\n\nClick the link below to begin:\n{{assessment_link}}\n\nThank you,\nThe HR Team`} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono text-slate-700 focus:ring-2 focus:ring-indigo-100 outline-none" data-id="element-281" data-mp="268gukroafimt" />
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end" data-id="element-282" data-mp="2f9yigw7xsom2">
              <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-283" data-mp="3cr5ivt70uh6j">
                <Save className="h-4 w-4 mr-2" data-id="element-284" data-mp="2shdtngywfbdf" />
                Save Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function PlatformSettingsPanel() {
  return <div className="p-6 max-w-2xl" data-id="element-285" data-mp="2ijpxqnpk3xto">
      <h2 className="text-lg font-semibold text-slate-900 mb-6" data-id="element-286" data-mp="2ikracwm80sdi">
        Platform Settings
      </h2>

      <div className="space-y-8" data-id="element-287" data-mp="2nit3kadd282p">
        <section data-id="element-288" data-mp="3b7vk1ew9duvm">
          <h3 className="text-sm font-medium text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-289" data-mp="3b7c0xuuvh0g0">
            Branding & Appearance
          </h3>
          <div className="space-y-4" data-id="element-290" data-mp="3g6f4fav7eylv">
            <div data-id="element-291" data-mp="31i2bynvbrxw4">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-292" data-mp="31ilotq64bw3a">
                Organization Name
              </label>
              <input type="text" defaultValue="Acme Corp" className="w-full max-w-md px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-slate-100 outline-none" data-id="element-293" data-mp="36fklogh62h56" />
            </div>
            <div data-id="element-294" data-mp="1nhpe9y965bsl">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-295" data-mp="1nh66399bm1rr">
                Primary Color (Hex)
              </label>
              <div className="flex items-center space-x-3" data-id="element-296" data-mp="1ihjsgypkyjif">
                <input type="text" defaultValue="#0f172a" className="w-32 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono focus:ring-2 focus:ring-slate-100 outline-none" data-id="element-297" data-mp="1x6z6rf7xcwh8" />
                <div className="w-8 h-8 rounded-md bg-slate-900 border border-slate-200" data-id="element-298" data-mp="zks0m0vz2w2r"></div>
              </div>
            </div>
          </div>
        </section>

        <section data-id="element-299" data-mp="1dlmdowtytl7r">
          <h3 className="text-sm font-medium text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-300" data-mp="1dhyk5xoisyt1">
            Assessment Defaults
          </h3>
          <div className="space-y-4" data-id="element-301" data-mp="2oyf1xxnjrrj2">
            <div className="flex items-center justify-between max-w-md" data-id="element-302" data-mp="2p1jdxh797rka">
              <div data-id="element-303" data-mp="2kx3bjpwun29t">
                <div className="text-sm font-medium text-slate-700" data-id="element-304" data-mp="2kzxsv8kkg6z0">
                  Require Minimum Respondents
                </div>
                <div className="text-xs text-slate-500" data-id="element-305" data-mp="1mlscbwv4oala">
                  Prevent report generation if under threshold
                </div>
              </div>
              <select className="px-3 py-1.5 border border-slate-200 rounded-md text-sm outline-none" data-id="element-306" data-mp="1rj0tbu4ji8ky">
                <option data-id="element-307" data-mp="1vkmc1w3r9h32">3 Respondents</option>
                <option data-id="element-308" data-mp="yx91paolvlq1">5 Respondents</option>
                <option data-id="element-309" data-mp="yyalq4nlnijw">No Minimum</option>
              </select>
            </div>
            <div className="flex items-center justify-between max-w-md" data-id="element-310" data-mp="13w2f733w7cq7">
              <div data-id="element-311" data-mp="17ww15qxryhgg">
                <div className="text-sm font-medium text-slate-700" data-id="element-312" data-mp="17wcnimf5qf88">
                  Enforce Anonymity
                </div>
                <div className="text-xs text-slate-500" data-id="element-313" data-mp="1cpz0kfp8gl4k">
                  Hide individual rater names on reports
                </div>
              </div>
              <ToggleRight className="h-6 w-6 text-emerald-500" data-id="element-314" data-mp="k6i9i1j3qkme" />
            </div>
          </div>
        </section>

        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-315" data-mp="cniqvy3gsx43">
          Save Changes
        </button>
      </div>
    </div>;
}
function AISettingsPanel() {
  return <div className="p-6 max-w-2xl" data-id="element-316" data-mp="3p70nx6ig1c6">
      <div className="flex items-center mb-6" data-id="element-317" data-mp="3uugllafndpex">
        <Sparkles className="h-5 w-5 text-indigo-600 mr-2" data-id="element-318" data-mp="3uvrol819qt4z" />
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-319" data-mp="3pvm369lle2vc">
          AI Features Configuration
        </h2>
      </div>

      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-8 text-sm text-indigo-800" data-id="element-320" data-mp="3l2j980cwkrem">
        AI features utilize OpenAI models to generate insights and summaries.
        Data is processed securely and is not used to train public models.
      </div>

      <div className="space-y-6" data-id="element-321" data-mp="3731hbc4b8y11">
        {[{
        title: 'Executive Summaries',
        desc: 'Auto-generate narrative summaries for completed assessments.',
        active: true
      }, {
        title: 'Blind Spot Detection',
        desc: 'Flag significant discrepancies between self and peer ratings.',
        active: true
      }, {
        title: 'Coaching Recommendations',
        desc: 'Suggest actionable coaching topics based on assessment results.',
        active: true
      }, {
        title: 'Sentiment Analysis',
        desc: 'Analyze open-text responses for overall sentiment trends.',
        active: false
      }].map((feature, i) => <div key={i} className="flex items-start justify-between border-b border-slate-100 pb-4 last:border-0" data-id="element-322" data-mp="25x3dnbebn3d" data-mpi={i}>
            <div data-id="element-323" data-mp="64mpc4ai2cym">
              <div className="text-sm font-medium text-slate-900" data-id="element-324" data-mp="3occ4bvatxgkf">
                {feature.title}
              </div>
              <div className="text-sm text-slate-500 mt-0.5" data-id="element-325" data-mp="3tbyic6yhf0ua">
                {feature.desc}
              </div>
            </div>
            {feature.active ? <ToggleRight className="h-6 w-6 text-indigo-600 shrink-0 ml-4 cursor-pointer" data-id="element-326" data-mp="39rpxymd11cmz" /> : <ToggleLeft className="h-6 w-6 text-slate-300 shrink-0 ml-4 cursor-pointer" data-id="element-327" data-mp="3jnt7ida7kdjy" />}
          </div>)}
      </div>
    </div>;
}
function AuditLogsPanel() {
  return <div className="flex flex-col h-full" data-id="element-328" data-mp="3er2rtr2bfjz2">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-329" data-mp="3snfz67d4y4zt">
        <div data-id="element-330" data-mp="3nod5tk6ipn7o">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-331" data-mp="3nov9h1vif3pm">Audit Logs</h2>
          <p className="text-sm text-slate-500" data-id="element-332" data-mp="696fgbk5hl5d">
            System-wide activity and security events.
          </p>
        </div>
        <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors bg-white" data-id="element-333" data-mp="1aln0yb545rh">
          Export CSV
        </button>
      </div>
      <div className="flex-1 overflow-auto" data-id="element-334" data-mp="b9ubxcr5eu6z">
        <table className="w-full text-sm text-left" data-id="element-335" data-mp="nq7zlk4x4ije">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-336" data-mp="nowmgtpl1yfm">
            <tr data-id="element-337" data-mp="ird3jghxwxhc">
              <th className="px-6 py-3 font-medium" data-id="element-338" data-mp="itzgxwrii77h">Timestamp</th>
              <th className="px-6 py-3 font-medium" data-id="element-339" data-mp="wqw3hm0ll2ik">User</th>
              <th className="px-6 py-3 font-medium" data-id="element-340" data-mp="rrtax11t0orh">Action</th>
              <th className="px-6 py-3 font-medium" data-id="element-341" data-mp="rqi4etgz52wo">Resource</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-342" data-mp="40e1kobm176q">
            {[{
            time: 'Oct 24, 14:32',
            user: 'Jane Doe',
            action: 'Generated Report',
            resource: 'Q3 Exec Summary'
          }, {
            time: 'Oct 24, 11:15',
            user: 'System',
            action: 'Sent Reminders',
            resource: 'Q3 Leadership Review (12)'
          }, {
            time: 'Oct 23, 09:45',
            user: 'Michael Scott',
            action: 'Created Assessment',
            resource: 'Sales Team Pulse'
          }, {
            time: 'Oct 22, 16:20',
            user: 'Jane Doe',
            action: 'Updated Settings',
            resource: 'AI Configuration'
          }, {
            time: 'Oct 22, 10:05',
            user: 'Sarah Connor',
            action: 'Imported Users',
            resource: 'Engineering Roster.csv'
          }].map((log, i) => <tr key={i} className="hover:bg-slate-50 text-slate-600" data-id="element-343" data-mp="2fnrazpwxyd5a" data-mpi={i}>
                <td className="px-6 py-4 whitespace-nowrap text-slate-500" data-id="element-344" data-mp="21q2qij6sd80r">
                  {log.time}
                </td>
                <td className="px-6 py-4 font-medium text-slate-900" data-id="element-345" data-mp="26r8lyortz9dz">
                  {log.user}
                </td>
                <td className="px-6 py-4" data-id="element-346" data-mp="v9yzdtdeo18g">{log.action}</td>
                <td className="px-6 py-4" data-id="element-347" data-mp="vbjubk3mtm6u">{log.resource}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}